import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Youtube, Phone, Mail, MapPin } from 'lucide-react';
import { useHotel } from '../context/HotelContext';

const Footer = () => {
  const { hotel } = useHotel();

  const hotelName = hotel?.hotelName || 'Hotel';
  const address = [hotel?.address, hotel?.city, hotel?.state, hotel?.country].filter(Boolean).join(', ') || '';
  const phone = hotel?.phone || hotel?.contactNumber || '';
  const email = hotel?.email || '';
  const logoUrl = hotel?.logoUrl || null;
  const facebookUrl = hotel?.facebookUrl || null;
  const youtubeUrl = hotel?.youtubeUrl || null;
  const primaryColor = hotel?.primaryColor || '#2563EB';

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Rooms', path: '/rooms' },
    { name: 'Gallery', path: '/gallery' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container">
        {/* Main Footer Content */}
        <div className="py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Hotel Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-2 mb-6">
                {logoUrl ? (
                  <img src={logoUrl} alt={hotelName} className="w-10 h-10 object-contain rounded-lg" />
                ) : (
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-xl">{hotelName.charAt(0).toUpperCase()}</span>
                  </div>
                )}
                <span className="text-2xl font-bold font-playfair">{hotelName}</span>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                {hotel?.description || `Experience luxury and comfort at ${hotelName}. We provide exceptional service and premium accommodations for business and leisure travelers alike.`}
              </p>
              <div className="space-y-3">
                {address && (
                  <div className="flex items-center space-x-3">
                    <MapPin size={18} className="text-blue-400 flex-shrink-0" />
                    <span className="text-gray-300">{address}</span>
                  </div>
                )}
                {phone && (
                  <div className="flex items-center space-x-3">
                    <Phone size={18} className="text-blue-400 flex-shrink-0" />
                    <span className="text-gray-300">{phone}</span>
                  </div>
                )}
                {email && (
                  <div className="flex items-center space-x-3">
                    <Mail size={18} className="text-blue-400 flex-shrink-0" />
                    <span className="text-gray-300">{email}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-xl font-semibold mb-6 font-playfair">Quick Links</h3>
              <nav className="space-y-3">
                {quickLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="block text-gray-300 hover:text-white transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Follow Us */}
            <div>
              <h3 className="text-xl font-semibold mb-6 font-playfair">Follow Us</h3>
              <div className="flex space-x-4">
                {facebookUrl && (
                  <a
                    href={facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg flex items-center justify-center transition-opacity duration-300 hover:opacity-80"
                    style={{ backgroundColor: primaryColor }}
                    aria-label="Facebook"
                  >
                    <Facebook size={20} />
                  </a>
                )}
                {youtubeUrl && (
                  <a
                    href={youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center hover:bg-red-700 transition-colors duration-300"
                    aria-label="YouTube"
                  >
                    <Youtube size={20} />
                  </a>
                )}
                {!facebookUrl && !youtubeUrl && (
                  <p className="text-gray-500 text-sm">No social links available.</p>
                )}
              </div>
              <div className="mt-6">
                <h4 className="text-lg font-medium mb-3">Newsletter</h4>
                <p className="text-gray-400 text-sm mb-4">
                  Subscribe to get special offers and updates.
                </p>
                {/* TODO: Newsletter signup form - will connect to API */}
                <div className="flex">
                  <input
                    type="email"
                    placeholder="Your email"
                    className="flex-grow px-4 py-2 bg-gray-800 border border-gray-700 rounded-l-lg focus:outline-none focus:border-blue-500 text-white"
                  />
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 transition-colors duration-300">
                    Subscribe
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-gray-800 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} {hotelName}. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              {phone && (
                <span className="text-gray-400">{phone}</span>
              )}
              {email && (
                <span className="text-gray-400">{email}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;